import {
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Input } from "barte-design-system";
import {
  CaretDownIcon,
  LockSimpleIcon,
  MagnifyingGlassIcon,
  XIcon,
} from "@phosphor-icons/react";

import styles from "./chip-select.module.css";

import type { ChipSelectOption, ChipSelectProps } from "./chip-select.type";

export const ChipSelect = ({
  label,
  value,
  options,
  onChange,
  onClear,
  placeholder,
  icon,
  badge,
  locked = false,
  clearable = false,
  disabled = false,
  searchPlaceholder,
  emptyText,
  className,
}: ChipSelectProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  const isInteractive = !locked && !disabled;

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value],
  );

  const normalize = (text: string) =>
    text
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase();

  const filteredOptions = useMemo(() => {
    const term = normalize(search.trim());
    if (!term) return options;

    return options.filter(
      (option) =>
        normalize(option.label).includes(term) ||
        normalize(option.description ?? "").includes(term) ||
        normalize(option.value).includes(term),
    );
  }, [options, search]);

  const handleClose = () => {
    setOpen(false);
    setSearch("");
    setActiveIndex(0);
  };

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: globalThis.MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleOpen = () => {
    if (!isInteractive) return;
    setOpen(true);
  };

  const handleSelect = (option: ChipSelectOption) => {
    onChange?.(option.value);
    handleClose();
  };

  const handleClear = (event: MouseEvent) => {
    event.stopPropagation();
    onClear?.();
  };

  const handleTriggerKeyDown = (event: KeyboardEvent) => {
    if (!isInteractive || open) return;

    if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
    }
  };

  const handleSearchKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      handleClose();
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, filteredOptions.length - 1));
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const option = filteredOptions[activeIndex];
      if (option) handleSelect(option);
    }
  };

  const displayBadge = badge ?? selectedOption?.badge;
  const displayValue = selectedOption?.label ?? placeholder ?? "";
  const showClear = clearable && !!value && !!onClear && isInteractive;

  return (
    <div ref={rootRef} className={`${styles.root} ${className ?? ""}`.trim()}>
      <div
        role="button"
        tabIndex={isInteractive ? 0 : -1}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-disabled={disabled}
        className={`${styles.chip} ${open ? styles.open : ""} ${!isInteractive ? styles.locked : ""}`.trim()}
        onClick={handleOpen}
        onKeyDown={handleTriggerKeyDown}
      >
        {icon && <span className={styles.icon}>{icon}</span>}

        <span className={styles.body}>
          <span className={styles.label}>
            {label}
            {locked && <LockSimpleIcon className={styles["lock-icon"]} weight="fill" />}
          </span>

          <span className={styles.value}>
            <span
              className={`${styles["value-text"]} ${!selectedOption ? styles.placeholder : ""}`.trim()}
            >
              {displayValue}
            </span>
            {displayBadge && <span className={styles.badge}>{displayBadge}</span>}
          </span>
        </span>

        {showClear && (
          <button
            type="button"
            className={styles["clear-button"]}
            onClick={handleClear}
            aria-label="clear"
          >
            <XIcon className={styles["clear-icon"]} />
          </button>
        )}

        {isInteractive && !showClear && <CaretDownIcon className={styles["caret-icon"]} />}
      </div>

      {open && (
        <div className={styles.dropdown} role="listbox">
          <div className={styles["dropdown-search"]}>
            <Input
              icon={MagnifyingGlassIcon}
              placeholder={searchPlaceholder}
              value={search}
              autoFocus
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
          </div>

          <ul className={styles["dropdown-list"]}>
            {filteredOptions.length === 0 && (
              <li className={styles["dropdown-empty"]}>
                {emptyText ?? "Nenhum resultado"}
              </li>
            )}

            {filteredOptions.map((option, index) => (
              <li key={option.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={option.value === value}
                  className={`${styles["dropdown-option"]} ${option.value === value ? styles.selected : ""} ${index === activeIndex ? styles.active : ""}`.trim()}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <span className={styles["option-label"]}>{option.label}</span>
                  {option.description && (
                    <span className={styles["option-description"]}>
                      {option.description}
                    </span>
                  )}
                  {option.badge && <span className={styles.badge}>{option.badge}</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
